/* eslint-disable no-underscore-dangle */
import { createAction } from '@reduxjs/toolkit';
import type { OrganizationState } from '../reducers/organization';
import { components, paths } from '../schema/docs/api/admin';
import type { AppThunk } from '../store/configureStore';
import { throwOnError } from './utils';

type FetchOrganizationResponse =
  paths['/api/organizations']['get']['responses']['200']['content']['application/json'];

type Paginated<T> = {
  rows: T[];
  count: number;
  pagination: {
    has_next: boolean;
    has_prev: boolean;
    max_page: number;
    per_page: number;
    current: number;
  };
};

type IssuerOrganization = {
  code: string;
  name: string;
  invoice_registration_number?: string;
  start_date?: string;
  end_date?: string;
  contact_post?: string;
  contact_name?: string;
  contact_email?: string;
  admin_screen?: boolean;
  pokepay_app?: boolean;
  mobile_sdk?: boolean;
  partner_sdk?: boolean;
  display_features?: components['schemas']['DisplayFeatures'];
};

type Organizations = OrganizationState['organizations'];
export const GET_ORGANIZATIONS = createAction<Organizations>('GET_ORGANIZATIONS');

type Organization = components['schemas']['Organization'];
type PrivateMoney = components['schemas']['PrivateMoney'];

type OwnOrganization = {
  bank_account: string;
  bank_account_holder_name: string;
  bank_account_type: string;
  bank_branch_code: string;
  bank_branch_name: string;
  bank_code: string;
  bank_name: string;
  code: string;
  contact_name: string;
  name: string;
  private_moneys: PrivateMoney[];
  bank_originator_code?: string | null;
  bank_originator_name?: string | null;
  // 法人番号 (13桁)。法人の適格請求書発行事業者登録番号は "T" + 法人番号 で表せる。未登録時は null。
  // (GET /api/organizations/{code} が返すが従来の型に未記載だった)
  corporate_number?: string | null;
};

type OrganizationPrivateMoneyFee = {
  organization: Organization;
  private_money: PrivateMoney;
  topup_fee_rate_percentage: number;
  payment_fee_rate_percentage: number;
  point_payment_fee_rate_percentage: number;
  system_fee: number;
  fixed_fee: number;
};

export type OrganizationIpRestrictionSettings = {
  ip_restriction: boolean;
  allowed_ips: AllowedIpAddr[];
  current_ip?: string;
};

type AllowedIpAddr = {
  ip: string;
  description: string;
};

export const createClearingSetting = (postData: Record<string, unknown> = {}) =>
  fetch(`/api/clearing-setting`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(postData),
  }).then((response) => response.json());

// TODO: returns "member" organizations by default, so should be able to specify a money.
export const fetchOrganizations = (query = '') =>
  fetch(`/api/organizations?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<FetchOrganizationResponse>);

export const getOrganizations =
  (query = ''): AppThunk =>
  (dispatch) =>
    fetchOrganizations(query).then((data) => dispatch(GET_ORGANIZATIONS(data)));

export const createMemberOrganization = (postData: Record<string, unknown> = {}) =>
  fetch('/api/organizations', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((response) => response.json());

export const getIssuerOrganizations = (query = '') =>
  fetch(`/api/issuer-organizations?${query}`).then(throwOnError<Paginated<IssuerOrganization>>);

export const getIssuerOrganization = (code: string) =>
  fetch(`/api/issuer-organizations/${code}`).then(throwOnError<IssuerOrganization>);

export const createIssuerOrganization = (postData: {
  code: string;
  name: string;
  invoice_registration_number?: string;
  start_date?: string;
  end_date?: string;
  contact_post?: string;
  contact_name?: string;
  contact_email?: string;
  admin_screen?: boolean;
  pokepay_app?: boolean;
  mobile_sdk?: boolean;
  partner_sdk?: boolean;
  display_features?: components['schemas']['DisplayFeatures'];
}) =>
  fetch('/api/issuer-organizations', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<IssuerOrganization>);

export const updateIssuerOrganization = (
  code: string,
  postData: {
    name: string;
    invoice_registration_number: string | null;
    start_date?: string;
    end_date?: string;
    contact_post: string | null;
    contact_name: string | null;
    contact_email: string | null;
    admin_screen?: boolean;
    pokepay_app?: boolean;
    mobile_sdk?: boolean;
    partner_sdk?: boolean;
    display_features?: components['schemas']['DisplayFeatures'];
  }
) =>
  fetch(`/api/issuer-organizations/${code}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<IssuerOrganization>);

// checkError is to accomodate different pages are handling the error different ways
// we should update this to standarzide late
// TODO: stardardize error handling
export const updateOrganization = (
  id: string,
  postData: Record<string, unknown> = {},
  checkError?: boolean
) =>
  fetch(`/api/organizations/${id}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((res) => (checkError ? throwOnError(res) : res.json()));

export const showOrganization = (id: string) =>
  fetch(`/api/organizations/${id}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError<OwnOrganization>);

export const getOrganizationBankPaySetting = (orgCode: string) =>
  fetch(`/api/organizations/${orgCode}/bankpay-setting`).then(
    throwOnError<{
      bankbook_merchant_name: string;
      iss_type: string;
      organization_code: string;
      p_id: string;
    }>
  );

export const getOrganizationPrivateMoneyFee = (orgCode: string, id: string) =>
  fetch(`/api/organizations/${orgCode}/private-moneys/${id}/fee`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError<OrganizationPrivateMoneyFee>);

export const createOrganizationPrivateMoneyFee = (
  orgCode: string,
  id: string,
  postData: {
    payment_fee_rate_percentage: number;
    topup_fee_rate_percentage: number;
    point_payment_fee_rate_percentage: number;
    system_fee: number;
    fixed_fee: number;
  }
) =>
  fetch(`/api/organizations/${orgCode}/private-moneys/${id}/fee`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<OrganizationPrivateMoneyFee>);

export const updateOrganizationPrivateMoneyFee = (
  orgCode: string,
  id: string,
  postData: {
    payment_fee_rate_percentage: number;
    topup_fee_rate_percentage: number;
    point_payment_fee_rate_percentage: number;
    system_fee: number;
    fixed_fee: number;
  }
) =>
  fetch(`/api/organizations/${orgCode}/private-moneys/${id}/fee`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<OrganizationPrivateMoneyFee>);

export const getAllowedIpAddrs = (orgCode: string) =>
  fetch(`/api/organizations/${orgCode}/allowed-ips`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError<OrganizationIpRestrictionSettings>);

export const saveAllowedIpAddrs = (orgCode: string, data: OrganizationIpRestrictionSettings) =>
  fetch(`/api/organizations/${orgCode}/allowed-ips`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<OrganizationIpRestrictionSettings>);

export type OrganizationApplicationSetting =
  components['schemas']['OrganizationApplicationSetting'];

export const getOrganizationApplicationSetting = (issuerOrganizationCode?: string) => {
  const query = issuerOrganizationCode
    ? `?issuer_organization_code=${encodeURIComponent(issuerOrganizationCode)}`
    : '';
  return fetch(`/api/organization-application-setting${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<OrganizationApplicationSetting>);
};

// `organization_application_setting_not_found` を null として返すラッパー。
// それ以外のエラーは throwOnError の挙動どおり Promise の reject を伝播する。
export const getOrganizationApplicationSettingOrNull = (
  issuerOrganizationCode?: string
): Promise<OrganizationApplicationSetting | null> =>
  getOrganizationApplicationSetting(issuerOrganizationCode).catch((e) =>
    Promise.resolve(e).then((err) => {
      if (err && typeof err === 'object' && err.type === 'organization_application_setting_not_found') {
        return null;
      }
      return Promise.reject(err);
    })
  );

export const createOrganizationApplicationSetting = (postData: {
  member_organization_code_prefix: string;
  issuer_organization_code?: string;
}) =>
  fetch('/api/organization-application-setting', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then(throwOnError<OrganizationApplicationSetting>);
